<script lang="ts">
  import type { ShellController } from './shell-controller.svelte';
  import { socketState } from '../socket-state.svelte';
  import { ASSET_PREFIX, sharedAtlas } from '../../assets/asset-store';
  import { atlasSignal } from '../../assets/atlas-signal.svelte';
  import { SHIP_NAMES, shipName } from '../../game/constants/ships';
  import ShipArt from '../ShipArt.svelte';
  import {
    SHOP_TABS,
    SHOP_TAB_LABELS,
    shipsTabLabel,
    type Product,
    type ShopTab,
  } from '../../game/constants/products';
  import { profileIconLabel, profileIconState } from '../../account/profile-ship';
  import { features } from '../../features/feature-state.svelte';
  import { CUSTOM_LOBBIES, SHIP_CUSTOMIZATION } from '../../features/overrides';

  const { shell }: { shell: ShellController } = $props();

  let tab = $state<ShopTab>('decal');
  let shipId = $state(0);
  let selectedId = $state(-1);

  const shipsTab = $derived(shipsTabLabel(features.enabled(SHIP_CUSTOMIZATION)));

  const EQUIP_PENDING_TIMEOUT_MS = 5000;
  let equipPending = $state(false);
  let equipPendingTimer: ReturnType<typeof setTimeout> | null = null;

  $effect(() => {
    void socketState.equipment;
    equipPending = false;
    if (equipPendingTimer !== null) {
      clearTimeout(equipPendingTimer);
      equipPendingTimer = null;
    }
    return () => {
      if (equipPendingTimer !== null) clearTimeout(equipPendingTimer);
    };
  });

  async function requestEquip(productId: number): Promise<void> {
    if (equipPending) return;
    equipPending = true;
    equipPendingTimer = setTimeout(() => {
      equipPending = false;
      equipPendingTimer = null;
    }, EQUIP_PENDING_TIMEOUT_MS);
    await shell.equip(productId);
    if (shell.shopError !== '') equipPending = false;
  }

  /** Negative price means never buyable; the Spectator/Moderator/Admin tags are priced that way. */
  const visible = $derived(
    socketState.products
      .filter((product) => product.tab === tab)
      .filter((product) => tab !== 'skin' || product.shipId === shipId)
      .filter((product) => product.purchasable || socketState.owns(product.id)),
  );

  const selected = $derived(visible.find((product) => product.id === selectedId) ?? null);

  const short = $derived(selected !== null && tooDear(selected));

  /**
   * Tile order is ascending product id, never server-response order: the original enumerated its
   * catalogue by integer-like key, which is always id-ascending regardless of arrival order.
   */
  const ordered = $derived([...visible].sort((a, b) => a.id - b.id));

  /**
   * ceil(n/2) columns is what caps the rack at 2 rows — overflow is reached by scrolling
   * sideways, not by wrapping into a third row.
   */
  const columns = $derived(Math.max(1, Math.ceil(ordered.length / 2)));

  const FADE = 34;

  let shelf: HTMLDivElement | undefined = $state();
  let atStart = $state(true);
  let atEnd = $state(true);

  function edges(): void {
    if (shelf === undefined) return;
    atStart = shelf.scrollLeft <= 1;
    atEnd = shelf.scrollLeft >= shelf.scrollWidth - shelf.clientWidth - 1;
  }

  $effect(() => {
    void ordered;
    edges();
  });

  function isEquipped(product: Product): boolean {
    return socketState.equipped(product.tab, shipId) === product.id;
  }

  function priceLabel(product: Product): string {
    if (!socketState.owns(product.id)) return product.purchasable ? String(product.price) : '';
    return isEquipped(product) ? 'equipped' : 'owned';
  }

  function tooDear(product: Product): boolean {
    return (
      !socketState.owns(product.id) && product.purchasable && socketState.credits < product.price
    );
  }

  function tileCss(product: Product) {
    void atlasSignal.generation;
    if (product.imagePath === '') return null;
    return sharedAtlas.spriteCssStretched(product.imagePath, 90);
  }

  const iconState = $derived(profileIconState(shell.profileShip, socketState.equipment, shipId));

  let iconPending = $state(false);

  $effect(() => {
    void shipId;
    void tab;
    shell.profileIconMessage = '';
    shell.profileIconError = '';
  });

  async function captureProfileIcon(): Promise<void> {
    iconPending = true;
    try {
      await shell.setProfileIcon(shipId);
    } finally {
      iconPending = false;
    }
  }

  function tileImage(product: Product): string {
    return product.imagePath === '' ? '' : `${ASSET_PREFIX}/${product.imagePath}`;
  }
</script>

<div class="wis-panel wis-panel--shop">
  <button
    class="wis-btn shop__back"
    type="button"
    onclick={() => {
      shell.navigate('pregame');
    }}>Back</button
  >

  {#if tab === 'skin'}
    <select class="wis-select wis-select--wide shop__ship" bind:value={shipId} aria-label="Ship">
      {#each SHIP_NAMES as name, id (id)}
        <option value={id}>{name}</option>
      {/each}
    </select>
  {/if}

  <span class="shop__credits" class:shop__credits--short={short} data-testid="shop-credits">
    <span class="shop__credits-label">Credits</span>
    {socketState.credits}
  </span>

  <div class="shop__left">
    {#each SHOP_TABS as name (name)}
      <button
        class="wis-tab shop__tab"
        class:shop__tab--soon={name === 'credits'}
        type="button"
        disabled={name === 'credits'}
        aria-pressed={tab === name}
        onclick={() => {
          tab = name;
          selectedId = -1;
        }}>{name === 'skin' ? shipsTab : SHOP_TAB_LABELS[name]}</button
      >
    {/each}

    {#if selected !== null}
      {@const item = selected}
      {#if socketState.owns(item.id)}
        <button
          class="wis-btn-buy shop__action"
          type="button"
          data-testid="shop-equip"
          disabled={equipPending}
          onclick={() => {
            void requestEquip(item.id);
          }}>{isEquipped(item) ? 'UnEquip' : 'Equip'}</button
        >
      {:else if item.purchasable}
        <button
          class="wis-btn-buy shop__action"
          type="button"
          data-testid="shop-buy"
          disabled={!shell.loggedIn || socketState.credits < item.price}
          onclick={() => {
            void shell.buy(item.id);
          }}>Buy</button
        >
      {/if}
    {/if}
  </div>

  <span
    class="shop__result"
    class:shop__result--bad={shell.shopError !== ''}
    data-testid="shop-result"
    role="status">{shell.shopError || shell.shopMessage}</span
  >

  {#if tab === 'skin' && features.enabled(CUSTOM_LOBBIES)}
    <button
      class="wis-btn shop__profile"
      type="button"
      data-testid="shop-profile-icon"
      disabled={iconPending || iconState === 'current' || !shell.loggedIn}
      onclick={() => {
        void captureProfileIcon();
      }}>{profileIconLabel(iconState)}</button
    >

    <span class="shop__profile-note" data-testid="shop-profile-note">
      {#if shell.profileIconError !== ''}
        <span class="shop__profile-note--bad">{shell.profileIconError}</span>
      {:else if shell.profileIconMessage !== ''}
        {shell.profileIconMessage}
      {:else if !shell.loggedIn}
        Log in to set a profile icon
      {:else if iconState === 'current'}
        This {shipName(shipId)}, with the skin and decal equipped now
      {:else if iconState === 'stale'}
        Captures this {shipName(shipId)}'s skin and decal
      {:else if iconState === 'other-ship'}
        Replaces {shipName(shell.profileShip?.shipId ?? 0)}
      {:else}
        Captures this {shipName(shipId)}'s skin and decal, to show beside your name in a lobby
      {/if}
    </span>
  {/if}

  <!-- After .shop__left: that column's box reaches the plate's bottom edge and swallows clicks
       on anything the markup puts under it. -->
  <button
    class="wis-btn-inline shop__redeem"
    type="button"
    data-testid="shop-redeem"
    onclick={() => {
      shell.navigate('redeem');
    }}>Redeem code</button
  >

  <div
    class="wis-hscroll shop__shelf"
    bind:this={shelf}
    style:--wis-fade-start="{atStart ? 0 : FADE}px"
    style:--wis-fade-end="{atEnd ? 0 : FADE}px"
    onscroll={edges}
  >
    {#if !socketState.catalogueLoaded}
      <p class="shop__note">Loading…</p>
    {:else if ordered.length === 0}
      <p class="shop__note">Nothing here.</p>
    {:else}
      <div class="shop__rack" style:grid-template-columns="repeat({columns}, 186px)">
        {#each ordered as product (product.id)}
          {@const css = tileCss(product)}
          <button
            class="wis-tile"
            class:wis-tile--owned={socketState.owns(product.id)}
            class:wis-tile--selected={product.id === selectedId}
            class:wis-tile--equipped={isEquipped(product)}
            type="button"
            onclick={() => {
              selectedId = product.id;
            }}
          >
            <span class="shop__art">
              {#if product.tab === 'skin'}
                <ShipArt class="shop__layer" team={0} shipId={product.shipId} size={90} />
              {/if}
              {#if css !== null}
                <span
                  class="shop__layer"
                  style:background-image="url({css.url})"
                  style:background-size={css.backgroundSize}
                  style:background-position={css.backgroundPosition}
                  style:width="{css.width}px"
                  style:height="{css.height}px"
                  style:display="inline-block"
                ></span>
              {:else if tileImage(product) !== ''}
                <img class="shop__layer" src={tileImage(product)} alt="" />
              {/if}
            </span>
            <span class="shop__name">{product.name}</span>
            <span class="shop__price" class:shop__price--dear={tooDear(product)}
              >{priceLabel(product)}</span
            >
          </button>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .shop__back {
    position: absolute;
    top: 25px;
    left: 705px;
    width: 150px;
    height: 45px;
    padding: 0;
    color: #f2f2f2;
    font-size: 16px;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  /* The left column's plate is broken by a cable strip at x=42..78 and a seam at x=188; this is
     the flat run between them. */
  .shop__credits {
    position: absolute;
    top: 470px;
    left: 82px;
    width: 102px;
    color: #f4f4f4;
    font-size: 19px;
    text-align: center;
    text-shadow: 0 1px 2px #000;
  }

  .shop__credits-label {
    display: block;
    font-size: 12px;
  }

  .shop__credits--short {
    color: #ff7263;
    animation: shop-short 300ms steps(1, end) 4;
  }

  @keyframes shop-short {
    0% {
      color: #ff7263;
    }
    50% {
      color: #f4f4f4;
    }
  }

  /* Under the plate: the shelf runs to its bottom edge and the left column is full. */
  .shop__result {
    position: absolute;
    top: 582px;
    left: 292px;
    width: 678px;
    color: #cfd6e4;
    font-size: 11px;
    text-align: center;
    text-shadow: 0 1px 2px #000;
  }

  .shop__result--bad {
    color: #ff7263;
  }

  .shop__ship {
    position: absolute;
    top: 32px;
    left: 400px;
  }

  .shop__left {
    position: absolute;
    top: 80px;
    left: 22px;
    width: 253px;
    bottom: 0;
  }

  .shop__tab {
    height: 59px;
    top: 4px;
    left: -28px;
    width: 285px;
    margin-bottom: -4px;
  }

  .shop__tab--soon {
    color: #fff;
    filter: brightness(0.58) saturate(0.5);
    cursor: default;
    text-shadow: 0 1px 2px #000;
  }

  .shop__action {
    position: absolute;
    top: 313px;
    left: 55px;
    width: 143px;
    height: 68px;
    padding: 0;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .shop__redeem {
    position: absolute;
    top: 545px;
    left: 82px;
    padding: 11px 14px;
  }

  .shop__profile {
    position: absolute;
    top: 538px;
    left: 46px;
    width: 160px;
    height: 36px;
    padding: 0;
    font-size: 10px;
    letter-spacing: 1px;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .shop__profile:disabled {
    filter: brightness(0.62) saturate(0.5);
    cursor: default;
  }

  .shop__profile-note {
    position: absolute;
    top: 575px;
    left: 26px;
    width: 220px;
    color: #cfd6e4;
    font-size: 10px;
    text-align: center;
    text-shadow: 0 1px 2px #000;
  }

  .shop__profile-note--bad {
    color: #ff7263;
  }

  .shop__shelf {
    position: absolute;
    top: 80px;
    left: 292px;
    width: 678px;
    height: 498px;
    mask-image: linear-gradient(
      to right,
      transparent 0,
      #000 var(--wis-fade-start, 0px),
      #000 calc(100% - var(--wis-fade-end, 0px)),
      transparent 100%
    );
  }

  /* Column count comes from script (`columns`) per tab; it is what holds the rack to 2 rows,
     with overflow scrolling sideways via .wis-hscroll. */
  .shop__rack {
    display: grid;
    grid-template-rows: repeat(2, 226px);
    grid-auto-flow: column;
    justify-content: start;
  }

  .shop__note {
    margin: 12px;
    color: #ccc;
    font-size: 14px;
    text-align: left;
  }

  .shop__art {
    display: grid;
    place-items: center;
    height: 90px;
    margin-top: 16px;
  }

  .shop__art :global(.shop__layer) {
    grid-area: 1 / 1;
    max-width: 90px;
    max-height: 90px;
  }

  .shop__price--dear {
    color: #7a2c2c;
  }

  .shop__price {
    text-shadow: 0 1px 2px rgb(0 0 0 / 45%);
  }

  .shop__name,
  .shop__price {
    display: block;
    position: relative;
    top: 10px;
    padding: 0 6px;
    font-size: 12px;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
</style>
